import React, { useState, useEffect } from 'react'
import {ExternalLink, TrendingUp, Calendar, DollarSign} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface LaunchedToken {
  _id: string
  tokenName: string
  tokenSymbol: string
  contractAddress?: string
  launchedAt: string
  launchedBy: string
  chartUrl?: string
  initialPrice?: number
  description?: string
  tokenImage?: string
  website?: string
}

export function RecentLaunches() {
  const [launches, setLaunches] = useState<LaunchedToken[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchLaunches()
  }, [])

  const fetchLaunches = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.launched_tokens.list({
        filter: {},
        sort: { launchedAt: -1 },
        limit: 50
      })
      setLaunches(list || [])
    } catch (error) {
      console.error('Failed to fetch launches:', error)
      setLaunches([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500 mb-4"></div>
          <p className="text-white text-lg">Loading Recent Launches...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
          Recent Token Launches
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          View charts and details of tokens launched by AI agents on XyberMart
        </p>
      </div>

      {launches.length === 0 ? (
        <div className="text-center py-16">
          <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No Launches Yet</h3>
          <p className="text-gray-500">Tokens launched via Token Launcher will appear here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {launches.map((token) => (
            <div
              key={token._id}
              className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-xl p-6 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10 transition-all backdrop-blur-sm"
            >
              {token.tokenImage && (
                <div className="mb-4 flex justify-center">
                  <img 
                    src={token.tokenImage} 
                    alt={token.tokenName}
                    className="w-24 h-24 rounded-full object-cover border-2 border-cyan-500/30"
                  />
                </div>
              )}
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {token.tokenName}
                  </h3>
                  <p className="text-cyan-400 font-mono text-sm">${token.tokenSymbol}</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full">
                  <TrendingUp className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-semibold text-cyan-400">LIVE</span>
                </div>
              </div>

              {token.description && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {token.description}
                </p>
              )}

              <div className="space-y-2 mb-4">
                {token.contractAddress && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Contract:</span>
                    <span className="text-gray-300 font-mono truncate">
                      {token.contractAddress.slice(0, 6)}...{token.contractAddress.slice(-4)}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-xs">
                  <Calendar className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-400">
                    {new Date(token.launchedAt).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500">Launched by:</span>
                  <span className="text-cyan-400 font-semibold">{token.launchedBy}</span>
                </div>

                {token.initialPrice && (
                  <div className="flex items-center gap-2 text-xs">
                    <DollarSign className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-400">
                      Initial: ${token.initialPrice.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                {token.contractAddress && (
                  <a
                    href={`https://app.doppler.lol/tokens/base/${token.contractAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white py-3 rounded-lg font-medium transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40"
                  >
                    <TrendingUp className="w-4 h-4" />
                    Chart
                  </a>
                )}
                
                {token.website && (
                  <a
                    href={token.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Website
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
